/**
 * Lightweight browser fingerprint — deterministic ~stable across reloads
 * on the same browser/device. Composed from:
 *   - User-Agent + language + platform
 *   - Screen resolution + color depth + pixel ratio
 *   - Timezone
 *   - Canvas paint hash (subpixel rendering varies by GPU/driver)
 *   - WebGL renderer string (when exposed)
 *   - Available browser fonts (presence check)
 *
 * Result is a 64-char SHA-256 hex digest of the joined components.
 * Never uniquely identifies a *person* — only the *browser instance*.
 * Cleared automatically when the user clears site data, which is the
 * privacy contract we want.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "bidora.devfp.v1";

function hexHash(str) {
  // SHA-256 via SubtleCrypto. Falls back to a 32-bit FNV-1a if SubtleCrypto
  // is missing (rare — old Android WebViews on http://).
  if (typeof crypto !== "undefined" && crypto.subtle) {
    return crypto.subtle
      .digest("SHA-256", new TextEncoder().encode(str))
      .then((buf) =>
        Array.from(new Uint8Array(buf))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")
      );
  }
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return Promise.resolve(h.toString(16).padStart(16, "0").repeat(4));
}

function canvasPaint() {
  try {
    const c = document.createElement("canvas");
    c.width = 200;
    c.height = 60;
    const ctx = c.getContext("2d");
    if (!ctx) return "no-ctx";
    ctx.textBaseline = "top";
    ctx.font = "16px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("bidorah.fp 🔥", 2, 15);
    ctx.fillStyle = "rgba(102,204,0,0.7)";
    ctx.fillText("bidorah.fp 🔥", 4, 17);
    return c.toDataURL().slice(-90); // last 90 chars is plenty entropy
  } catch (_e) {
    return "no-canvas";
  }
}

function webglRenderer() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return "no-webgl";
    const dbgInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbgInfo) {
      return [
        gl.getParameter(dbgInfo.UNMASKED_VENDOR_WEBGL),
        gl.getParameter(dbgInfo.UNMASKED_RENDERER_WEBGL),
      ].join("|");
    }
    return gl.getParameter(gl.VENDOR);
  } catch (_e) {
    return "no-webgl";
  }
}

function fontFingerprint() {
  // Presence test for a handful of common fonts. Speed > completeness.
  if (!document.fonts || !document.fonts.check) return "?";
  const fonts = ["Arial", "Helvetica", "Times New Roman", "Courier New", "Georgia", "Comic Sans MS", "Tahoma", "Verdana", "Impact", "Trebuchet MS"];
  return fonts.filter((f) => document.fonts.check(`12px "${f}"`)).join(",");
}

async function computeFingerprint() {
  const parts = [
    navigator.userAgent || "",
    navigator.language || "",
    (navigator.languages || []).join(","),
    navigator.platform || "",
    String(navigator.hardwareConcurrency || ""),
    String(navigator.maxTouchPoints || ""),
    `${window.screen.width}x${window.screen.height}@${window.screen.colorDepth}|dpr${window.devicePixelRatio || 1}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    canvasPaint(),
    webglRenderer(),
    fontFingerprint(),
  ];
  const joined = parts.join("||");
  return await hexHash(joined);
}

/**
 * Returns the device fingerprint (string), or null while it's being
 * computed on first paint. Cached in localStorage so the same browser
 * always reports the same value — even across the canvas/webgl noise
 * that small browser updates can introduce.
 */
export function useDeviceFingerprint() {
  const [fp, setFp] = useState(() => {
    try {
      return typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    } catch (_e) {
      return null;
    }
  });

  useEffect(() => {
    if (fp) return; // already cached
    let cancelled = false;
    (async () => {
      try {
        const computed = await computeFingerprint();
        if (cancelled) return;
        try {
          localStorage.setItem(STORAGE_KEY, computed);
        } catch (_e) {}
        setFp(computed);
      } catch (_e) {
        /* fingerprinting failed — backend treats null fp as anonymous */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fp]);

  return fp;
}

export function getCachedFingerprint() {
  try {
    return typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  } catch (_e) {
    return null;
  }
}
